angular.module('elogbooksServices').service('Auth0Service', function (
    angularAuth0,
    apiClient,
    userManager,
    modulesService,
    $window,
    $state,
    $rootScope,
    $http,
    $timeout,
    $location,
    API_URL
) {
    'use strict';

    // angular-auth0-spa-js v4 is built on the old auth0-spa-js v1 API

    var vm = this;
    vm.getCurrentUserEmail = getCurrentUserEmail;
    vm.isUserLinked = isUserLinked;
    vm.getSessionCredentials = getSessionCredentials;
    vm.handleLoginResult = handleLoginResult;
    vm.performSsoLogin = performSsoLogin;

    // Thin wrapper around angularAuth0 service from angular-auth0-spa-js
    // This service exists for backward compatibility
    // Public methods - delegate to angularAuth0
    vm.isAuthenticated = angularAuth0.isAuthenticated;
    vm.getUser = angularAuth0.getUser;
    vm.loginWithRedirect = angularAuth0.loginWithRedirect;
    vm.logout = logout;

    function logout() {
        $window.localStorage.removeItem('ssoUsername');
        $window.localStorage.removeItem('auth0LoginInProgress');
        $window.localStorage.removeItem('ssoDestination');
        return angularAuth0.logout();
    }
    vm.getTokenWithPopup = angularAuth0.getTokenWithPopup;
    vm.handleRedirectCallback = angularAuth0.handleRedirectCallback;
    vm.handleAuth0LoginRedirect = handleAuth0LoginRedirect;

    // Handle Auth0 OAuth callback on redirect back to app
    // Called from $stateChangeStart in app.run
    async function handleAuth0LoginRedirect(event) {
        // Auth0 can redirect back with an error (e.g. MFA misconfig,
        // "No MFA factors enabled for enrollment") and NO `code`. That case has
        // no code+state, so hasOAuthCallbackParams() is false — without this
        // branch the user would be stranded on the auth0-processing spinner.
        const oauthError = getOAuthError();
        if (oauthError) {
            console.error('Auth0 redirect returned an error:', oauthError.error, oauthError.description);
            $window.localStorage.removeItem('auth0LoginInProgress');
            $window.localStorage.removeItem('ssoUsername');
            return handleAuthError(new Error('SSO_AUTH_REDIRECT_ERROR'));
        }

        if (!hasOAuthCallbackParams()) {
            return false;
        }

        try {
            const { user, jwtToken } = await authenticateWithAuth0();
            const username = $window.localStorage.getItem('ssoUsername');

            if (!username) {
                throw new Error('SSO_LOGIN_FAILED');
            }

            const linked = await isUserLinked(user, jwtToken, username);

            $window.localStorage.removeItem('auth0LoginInProgress');

            if (linked) {
                const ssoLoginResult = await performSsoLogin(user, jwtToken, username);
                $window.localStorage.removeItem('ssoUsername');
                await handleLoginResult(ssoLoginResult);
            } else {
                // First-time link: keep ssoUsername for the password screen.
                $state.go('login.sso-link-password');
            }

            return true;
        } catch (error) {
            console.error('Auth0 callback processing failed:', error);
            $window.localStorage.removeItem('auth0LoginInProgress');
            throw handleAuthError(error);
        }
    }

    // The OAuth callback query string is stripped from window.location BEFORE
    // AngularJS bootstraps (see the inline script in index.html), because
    // hashbang $location bakes the pre-hash query into its appBase and keeps
    // re-adding it. The original URL is stashed on window.__auth0Callback; read
    // the callback params from there, not from the (now-clean) live URL.
    function getCallbackSearch() {
        const url = $window.__auth0Callback || '';
        const queryStart = url.indexOf('?');
        if (queryStart === -1) {
            return '';
        }
        const afterQuery = url.substring(queryStart + 1);
        const fragmentStart = afterQuery.indexOf('#');
        return fragmentStart === -1 ? afterQuery : afterQuery.substring(0, fragmentStart);
    }

    function hasOAuthCallbackParams() {
        const search = getCallbackSearch();
        return search.indexOf('code=') !== -1 && search.indexOf('state=') !== -1;
    }

    // Returns the Auth0 error from the stashed callback URL, or null if there
    // isn't one. An Auth0 error redirect looks like
    // ?error=…&error_description=…&state=… (no `code`).
    function getOAuthError() {
        const params = new URLSearchParams(getCallbackSearch());
        const error = params.get('error');
        if (!error) {
            return null;
        }
        return {
            error: error,
            description: params.get('error_description') || ''
        };
    }

    async function authenticateWithAuth0() {
        // Pass the stashed callback URL explicitly: the live window.location no
        // longer has the code/state query (stripped pre-bootstrap), so the SDK's
        // default of window.location.href would throw "no query params".
        await angularAuth0.handleRedirectCallback($window.__auth0Callback);
        const isAuthenticated = await angularAuth0.isAuthenticated();

        if (!isAuthenticated) {
            throw new Error('NOT_AUTHENTICATED');
        }

        // Get the raw ID Token JWT for the backend
        const idTokenClaims = await angularAuth0.getIdTokenClaims();
        const jwtToken = idTokenClaims?.__raw;

        const user = await angularAuth0.getUser();

        if (!user || !jwtToken) {
            throw new Error('NO_USER_DATA');
        }

        return { user, jwtToken };
    }

    async function performSsoLogin(user, token, username, password) {
        if (!username) {
            throw new Error('SSO_LOGIN_FAILED');
        }

        // Use raw $http (like userManager.login) so the backend error code
        // (e.g. USER_AUTH_TOO_MANY_ATTEMPTS, SSO_LOGIN_FAILED) reaches the caller.
        // apiClient.create swallows non-201 responses into `false`.
        let response;
        try {
            response = await $http.post(API_URL + '/sso/login', {
                idToken: user.sub,
                jwtToken: token,
                username: username,
                email: user.email,
                password: password || '',
                source: 'sso'
            });
        } catch (failResponse) {
            var message = (failResponse && failResponse.data && failResponse.data.message)
                ? failResponse.data.message
                : 'SSO_LOGIN_FAILED';
            throw new Error(message);
        }

        if (!response || !response.data || !response.data.token) {
            throw new Error('SSO_LOGIN_FAILED');
        }

        return response.data;
    }

    async function handleLoginResult(response) {
        if (!response || !response.token) {
            throw new Error('INVALID_LOGIN_RESPONSE');
        }

        $window.localStorage.setItem("token", response.token);
        $window.localStorage.setItem("tokenExpiration", response.expiration * 1000);
        $window.localStorage.setItem("userEmail", response.email);
        $window.localStorage.setItem("lastEmailVerificationSentAt", response.last_email_verification_sent_at);
        $window.localStorage.setItem("userPreferencesUrl", "/user/preferences");
        $window.localStorage.removeItem("recaptchaRequired");

        // Consume any bookmarked destination stashed before the Auth0 redirect
        // (set in loginStep1's loginWithSso). Read-and-clear so it can't leak
        // into a later, unrelated login.
        var destination = $window.localStorage.getItem('ssoDestination');
        $window.localStorage.removeItem('ssoDestination');
        // A malformed destination (hand-edited or truncated link) makes decode()
        // throw; drop it rather than fail a login that has already succeeded.
        var destinationUrl = null;
        if (destination) {
            try {
                destinationUrl = destination.decode();
            } catch (error) {
                console.error('Ignoring undecodable SSO destination:', error);
            }
        }

        // Fetch root once for the whole login transition: preloading modules and the
        // user skips the router session-bootstrap, and priming them skips the
        // dashboard resolves' own fetches.
        const rootUrl = $window.localStorage.getItem('originalUser') === null ? '/?dashboard=1' : '/';
        const rootResource = await apiClient.get(rootUrl, null, false);

        if (rootResource && rootResource.getLink && rootResource.getLink('user') && rootResource.getLink('token')) {
            $window.localStorage.setItem('userResource', rootResource.getLink('user'));
            $window.localStorage.setItem('tokenLink', rootResource.getLink('token'));
            // getUserResourceAndTokenLink() used to reset this as a side effect.
            userManager.skippedChangePassword = false;

            // Best-effort: the token is already persisted, so a failure here must not
            // fail the login. Left unprimed, the dashboard resolves just refetch.
            try {
                await modulesService.load(rootResource);
                const loggedInUser = await userManager.getUser();

                userManager.primeRootResource(rootResource);
                userManager.primeUser(loggedInUser);
            } catch (error) {
                console.warn('Login priming failed; the dashboard resolves will refetch:', error);
            }

            // '/' is the template-less root state, which only bounces on to the
            // dashboard; reached from auth0-processing that login never completes and
            // the user is left on the loader. Go to the dashboard directly instead.
            if (destinationUrl && destinationUrl !== '/') {
                // Native async continuation runs outside a digest, so $location
                // alone wouldn't trigger routing; $timeout bounces back in.
                $timeout(function () {
                    $location.url(destinationUrl);
                });
            } else {
                $state.go("dashboard.user.dashboard", {}, { reload: true });
            }
        } else if (rootResource) {
            $state.go("email.verification");
        } else {
            clearLocalTokens();
            $state.go('login.step1');
        }
    }

    function handleAuthError(error) {
        clearLocalTokens();
        $window.localStorage.removeItem('ssoDestination');

        const errorMessage = error ? (error.message || error) : 'THERE_WAS_AN_ERROR';

        $rootScope.loginFailMsg = errorMessage;

        $timeout(function () {
            $state.go('login.step1');
        });

        return { message: errorMessage };
    }

    function clearLocalTokens() {
        $window.localStorage.removeItem("token");
        $window.localStorage.removeItem("tokenExpiration");
    }

    async function getCurrentUserEmail() {
        try {
            const isAuth = await angularAuth0.isAuthenticated();
            if (!isAuth) {
                return null;
            }
            const user = await angularAuth0.getUser();
            return user?.email || null;
        } catch (error) {
            return null;
        }
    }

    async function isUserLinked(user, jwtToken, username) {
        // Raw $http so the body is read directly regardless of 200/201 status;
        // apiClient.create only returns a body on 201 and swallows errors.
        // Any rejection (network, 4xx/5xx) propagates and is hard-required by the
        // caller, which routes to login.step1.
        // The backend derives identity solely from the verified JWT `sub`, so only
        // jwtToken is sent; it responds { isUserLinked: bool } (matching the sibling
        // GET /sso/is-user-linked endpoint).
        let response;
        try {
            response = await $http.post(API_URL + '/sso/is-linked', {
                jwtToken: jwtToken
            });
        } catch (failResponse) {
            var message = (failResponse && failResponse.data && failResponse.data.message)
                ? failResponse.data.message
                : 'SSO_LINK_CHECK_FAILED';
            throw new Error(message);
        }

        if (!response || !response.data || typeof response.data.isUserLinked !== 'boolean') {
            throw new Error('SSO_LINK_CHECK_FAILED');
        }

        return response.data.isUserLinked;
    }

    async function getSessionCredentials() {
        const isAuth = await angularAuth0.isAuthenticated();
        if (!isAuth) {
            return null;
        }

        const idTokenClaims = await angularAuth0.getIdTokenClaims();
        const jwtToken = idTokenClaims?.__raw;
        const user = await angularAuth0.getUser();

        if (!user || !jwtToken) {
            return null;
        }

        return { user, jwtToken };
    }
});
